/// <reference types="@types/google.maps" />
import { useEffect, useState } from 'react';
import { GOOGLE_MAPS_API_KEY } from '@/config/google-maps';

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-script';

const loadGoogleMapsScript = (apiKey: string, onLoad: () => void, onError: () => void) => {
  const desiredSrc = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
  const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null;

  if (existingScript) {
    const currentSrc = existingScript.getAttribute('src') || '';
    const scriptLoaded = existingScript.dataset.status === 'loaded' && !!window.google?.maps;
    const scriptFailed = existingScript.dataset.status === 'error';
    const wrongScript = currentSrc !== desiredSrc;

    if (scriptLoaded) {
      onLoad();
      return;
    }

    if (!scriptFailed && !wrongScript) {
      existingScript.addEventListener('load', onLoad);
      existingScript.addEventListener('error', onError);
      return;
    }

    existingScript.remove();
  }

  const script = document.createElement('script');
  script.id = GOOGLE_MAPS_SCRIPT_ID;
  script.src = desiredSrc;
  script.async = true;
  script.defer = true;
  script.dataset.status = 'loading';

  script.addEventListener('load', () => {
    script.dataset.status = 'loaded';
    onLoad();
  });

  script.addEventListener('error', () => {
    script.dataset.status = 'error';
    onError();
  });

  document.head.appendChild(script);
};

export const useGoogleMaps = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const handleLoaded = () => {
      if (mounted) {
        setIsLoaded(true);
        setLoadError(null);
      }
    };

    const handleError = () => {
      if (mounted) {
        setLoadError('Failed to load Google Maps');
      }
    };

    const resolveApiKey = async () => {
      if (window.google?.maps) {
        handleLoaded();
        return;
      }

      if (GOOGLE_MAPS_API_KEY) {
        loadGoogleMapsScript(GOOGLE_MAPS_API_KEY, handleLoaded, handleError);
        return;
      }

      try {
        const baseUrl = import.meta.env.VITE_SUPABASE_URL;
        const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

        const response = await fetch(`${baseUrl}/functions/v1/google-maps-browser-key`, {
          headers: {
            apikey: publishableKey,
          },
        });

        if (!response.ok) {
          handleError();
          return;
        }

        const data = await response.json();
        if (!data?.apiKey) {
          handleError();
          return;
        }

        loadGoogleMapsScript(data.apiKey, handleLoaded, handleError);
      } catch (error) {
        console.error('Google Maps key fetch failed', error);
        handleError();
      }
    };

    resolveApiKey();

    return () => {
      mounted = false;
    };
  }, []);

  return { isLoaded, loadError };
};
